import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { useContext, useEffect, useMemo, useState } from 'react';
import { APIContext } from '../../Contexts/APIContext';
import SingleFileUpload from '../../Utilities/SingleFileUpload';
import MaintenanceDialogTitle from './MaintenanceDialogTitle';
import { ReleaseVersion } from './ReleaseManagementTypes';

type Props = { open: boolean; onClose: () => void };
type OperationMode = 'Copy' | 'Move';

type Preview = {
    operationMode: OperationMode;
    sourceVersion: ReleaseVersion;
    targetVersion: ReleaseVersion;
    uploadedRows: number;
    sitesToCopy: string[];
    alreadyInTarget: string[];
    missingFromSource: string[];
    invalidReferences: string[];
    duplicateReferences: string[];
    scheduledLocal: string;
    startsImmediately: boolean;
    hasActiveRun: boolean;
};

const ReferenceSummary = ({ label, values, severity }: {
    label: string;
    values: string[];
    severity: 'info' | 'warning' | 'error';
}) => values.length > 0 ? <Alert severity={severity}>
    <strong>{label} ({values.length}):</strong> {values.slice(0, 12).join(', ')}
    {values.length > 12 ? `, and ${values.length - 12} more` : ''}
</Alert> : null;

export default function MoveSelectedSitesDialog({ open, onClose }: Props) {
    const { get, post, showMessage } = useContext(APIContext);
    const [versions, setVersions] = useState<ReleaseVersion[]>([]);
    const [mode, setMode] = useState<OperationMode>('Copy');
    const [sourceId, setSourceId] = useState(0);
    const [targetId, setTargetId] = useState(0);
    const [file, setFile] = useState<File | null>(null);
    const [preview, setPreview] = useState<Preview | null>(null);
    const [acknowledged, setAcknowledged] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const futureVersions = useMemo(
        () => versions.filter(version => !version.isCurrent && new Date(version.start).getTime() > Date.now()),
        [versions]
    );
    const availableVersions = mode === 'Move' ? futureVersions : versions;
    const verb = mode === 'Move' ? 'move' : 'copy';

    const resetPreview = () => {
        setPreview(null);
        setAcknowledged(false);
        setConfirming(false);
    };

    const selectDefaults = (items: ReleaseVersion[], nextMode: OperationMode) => {
        const available = nextMode === 'Move'
            ? items.filter(version => !version.isCurrent && new Date(version.start).getTime() > Date.now())
            : items;
        const source = nextMode === 'Copy' ? items.find(item => item.isCurrent) ?? available[0] : available[0];
        const target = available.find(item => item.id !== source?.id);
        setSourceId(source?.id ?? 0);
        setTargetId(target?.id ?? 0);
    };

    useEffect(() => {
        if (!open) return;
        setMode('Copy');
        resetPreview();
        get('Admin', 'GetReleaseVersions', (items: ReleaseVersion[]) => {
            setVersions(items);
            selectDefaults(items, 'Copy');
        });
    }, [open]);

    const formData = () => {
        const data = new FormData();
        if (file) data.append('file', file);
        data.append('sourceVersionId', sourceId.toString());
        data.append('targetVersionId', targetId.toString());
        data.append('mode', mode);
        return data;
    };

    const review = async () => {
        if (!file) return;
        setSubmitting(true);
        await post('Admin', 'PreviewSelectedSiteMigration', formData(), setPreview);
        setSubmitting(false);
    };

    const schedule = async () => {
        if (!file) return;
        setSubmitting(true);
        await post('Admin', 'ScheduleSelectedSiteMigration', formData(), () => {
            showMessage('success', `${preview?.sitesToCopy.length ?? 0} selected sites were scheduled to ${verb}.`);
            close(true);
        });
        setSubmitting(false);
    };

    const close = (completed = false) => {
        if (submitting && !completed) return;
        setFile(null);
        setPreview(null);
        setAcknowledged(false);
        setConfirming(false);
        onClose();
    };

    const hasStrictErrors = !!preview && mode === 'Move' && (
        preview.invalidReferences.length > 0 || preview.duplicateReferences.length > 0 ||
        preview.missingFromSource.length > 0 || preview.alreadyInTarget.length > 0
    );
    const canReview = !!file && sourceId > 0 && targetId > 0 && sourceId !== targetId && !submitting;
    const canSchedule = !!preview && preview.sitesToCopy.length > 0 && !preview.hasActiveRun &&
        !hasStrictErrors && acknowledged && !submitting;

    return <Dialog open={open} onClose={() => close()} maxWidth="md" fullWidth>
        <MaintenanceDialogTitle
            icon={<ContentCopyOutlinedIcon />}
            title="Copy or move selected sites"
            description="Upload a CSV and choose whether source data is retained"
        />
        <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
                <ToggleButtonGroup exclusive fullWidth value={mode} disabled={submitting}
                    onChange={(_, value: OperationMode | null) => {
                        if (!value) return;
                        setMode(value);
                        selectDefaults(versions, value);
                        resetPreview();
                    }}>
                    <ToggleButton value="Copy">Copy</ToggleButton>
                    <ToggleButton value="Move">Move</ToggleButton>
                </ToggleButtonGroup>
                <Alert severity={mode === 'Move' ? 'warning' : 'info'}>
                    Use a CSV with a <strong>Reference</strong> column, or references in the first column. {mode === 'Move'
                        ? 'Move is available only between future versions. Every CSV row must be clean, and source records are permanently deleted only after the target copy is verified.'
                        : 'Source records are retained and existing target sites are skipped.'}
                </Alert>
                {mode === 'Move' && futureVersions.length < 2 && <Alert severity="error">
                    At least two future data versions are required to move sites.
                </Alert>}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField select fullWidth label="Source version" value={sourceId} disabled={submitting || availableVersions.length === 0}
                        onChange={event => {
                            const nextSourceId = Number(event.target.value);
                            setSourceId(nextSourceId);
                            if (nextSourceId === targetId)
                                setTargetId(availableVersions.find(version => version.id !== nextSourceId)?.id ?? 0);
                            resetPreview();
                        }}>
                        {availableVersions.map(version => <MenuItem key={version.id} value={version.id}>{version.versionName}</MenuItem>)}
                    </TextField>
                    <TextField select fullWidth label="Target version" value={targetId} disabled={submitting || availableVersions.length === 0}
                        onChange={event => { setTargetId(Number(event.target.value)); resetPreview(); }}>
                        {availableVersions.map(version => <MenuItem key={version.id} value={version.id} disabled={version.id === sourceId}>{version.versionName}</MenuItem>)}
                    </TextField>
                </Stack>
                <SingleFileUpload
                    file={file}
                    acceptedTypes=".csv,text/csv"
                    onDrop={newFile => { setFile(newFile); resetPreview(); }}
                    onDelete={() => { setFile(null); resetPreview(); }}
                />

                {preview && <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={1.25}>
                        <Typography variant="h6">CSV review</Typography>
                        <Typography>
                            <strong>{preview.sitesToCopy.length}</strong> of {preview.uploadedRows} uploaded rows will be {mode === 'Move' ? 'moved' : 'copied'} from <strong>{preview.sourceVersion.versionName}</strong> to <strong>{preview.targetVersion.versionName}</strong>.
                        </Typography>
                        <Typography variant="body2">{preview.startsImmediately ? 'Starts immediately because 22:00 UK time has passed.' : `Scheduled start: ${preview.scheduledLocal} (Europe/London)`}</Typography>
                        {preview.hasActiveRun && <Alert severity="error">Another Copy, Move, or Bulk Delete is active for one of these versions.</Alert>}
                        <ReferenceSummary label={mode === 'Move' ? 'Already represented in target (blocks Move)' : 'Already in target (skipped)'} values={preview.alreadyInTarget} severity={mode === 'Move' ? 'error' : 'info'} />
                        <ReferenceSummary label="Not found in source" values={preview.missingFromSource} severity="error" />
                        <ReferenceSummary label="Invalid references" values={preview.invalidReferences} severity="error" />
                        <ReferenceSummary label={mode === 'Move' ? 'Duplicate references (blocks Move)' : 'Duplicate references (ignored)'} values={preview.duplicateReferences} severity={mode === 'Move' ? 'error' : 'warning'} />
                        {hasStrictErrors && <Alert severity="error">Move cannot be scheduled until every CSV entry passes validation.</Alert>}
                    </Stack>
                </Paper>}

                {preview && <FormControlLabel
                    control={<Checkbox checked={acknowledged} onChange={event => setAcknowledged(event.target.checked)} />}
                    label={mode === 'Move'
                        ? 'I understand the source records will be permanently deleted after each target copy is verified.'
                        : 'I have reviewed the CSV and understand source data is retained and target sites are not overwritten.'}
                />}
            </Stack>
        </DialogContent>
        <DialogActions>
            <Button onClick={() => close()} disabled={submitting}>Cancel</Button>
            {!preview && <Button variant="contained" onClick={review} disabled={!canReview}>Review CSV</Button>}
            {preview && <Button variant="outlined" onClick={review} disabled={!canReview}>Refresh review</Button>}
            {preview && <Button variant="contained" color="warning" onClick={() => setConfirming(true)} disabled={!canSchedule}>Review schedule</Button>}
        </DialogActions>

        <Dialog open={confirming} onClose={() => !submitting && setConfirming(false)} maxWidth="sm" fullWidth>
            <MaintenanceDialogTitle icon={<ReportProblemOutlinedIcon />} title="Final confirmation"
                description={`Confirm the selected-site ${verb}`} tone="warning" />
            <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    <Alert severity="warning">
                        {mode === 'Move'
                            ? <>Move <strong>{preview?.sitesToCopy.length}</strong> sites and permanently remove their verified source records?</>
                            : <>Copy <strong>{preview?.sitesToCopy.length}</strong> sites while retaining all source records?</>}
                    </Alert>
                    <Typography variant="body2">
                        {preview?.sourceVersion.versionName} → {preview?.targetVersion.versionName}. {preview?.startsImmediately
                            ? 'The job will be enqueued immediately.'
                            : `The job is scheduled for ${preview?.scheduledLocal} UK time.`}
                    </Typography>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setConfirming(false)} disabled={submitting}>Back</Button>
                <Button onClick={schedule} disabled={submitting} variant="contained" color="warning">Schedule {mode}</Button>
            </DialogActions>
        </Dialog>
    </Dialog>;
}
